import React, { Component } from 'react';
import { Input, Form, Button } from 'antd';
import { Link } from 'react-router-dom';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import Icon from '@ant-design/icons';
import { IWithGoogleReCaptchaProps, withGoogleReCaptcha } from 'react-google-recaptcha-v3';import { RECAPTCHA_ERROR } from '../signUp/constants';
import { RecaptchaActions } from '../signUp/types';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_FALLBACK_SITE_KEY } from '../../../config';

import sightXLogo from '../../../assets/sightXLogoUiRefresh.png';
import { ReactComponent as EyeOffSVG } from '../../../assets/sightx-icons/svg/eye-off.svg';
import { ReactComponent as EyeOnSVG } from '../../../assets/sightx-icons/svg/eye-open.svg';
import { IUserLoginRequest, userLogin, userLogout } from '../../../slices/auth/login.thunks';
import { getActiveView } from '../../../slices/adminDashboard/view';
import { RootState } from '../../../slices/rootReducer';
import {
  deleteRedirectUrl,
  selectors as authSelectors,
  logoutSession,
  clearShowReCaptchaV2ForLogin
} from '../../../slices/auth';
import { fetchWhoAmI } from '../../../slices/auth/login.thunks';
import { IUser } from '../../../slices/users/user';
import { showErrorMessage, showSuccessMessage } from '../../../slices/notifications';
import { SocialLoginButtons } from './socialLoginButtons';
import { getQueryParameterFromPath } from '../../common/utils/getQueryParameterFromPath';
import { CognitoAction } from '../../../services/cognito';
import { CuriousBanner } from '../../common/curiousBanner';
import { SingleSignOnForm } from './singleSignOnForm';
import { isNetquestClient, isPureProfileClient } from '../../../utils/clients';
import { LoginForIncrowd } from './loginForIncrowd';
import { getUrlToRedirect, isNoneCoreUrl } from '../utils/getUrlToRedirect';
import { SignUpDivider } from '../signUp/SignUpDivider';
import { PrimaryButton } from '../../common/primaryButton';
import { HubSpotExclusionField } from '../hubspotExclusionField';
import { Logo } from './logo/Logo';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { FormInstance } from 'antd/lib/form';

import styles from './login.module.scss';

const validateMessages = {
  required: 'Required',
  types: {
    email: 'Invalid'
  }
};

interface ILoginProps extends RouteComponentProps {
  userLogin: (loginValues: IUserLoginRequest) => void;
  userLogout: () => void;
  isLoggedIn: boolean;
  isLoading: boolean;
  is2faEnabled: boolean;
  fetchWhoAmI: () => void;
  showSuccessMessage: (message: string) => void;
  userId: string | null;
  token: string;
  redirectUrl: string;
  activeView: string;
  user: IUser | null;
  error: string | null;
  cognitoCode: string;
  cognitoState: string;
  deleteRedirectUrl: typeof deleteRedirectUrl;
  logoutSession: () => void; // TODO, separate dispatch props
  showReCaptchaV2: boolean;
  clearShowReCaptchaV2ForLogin: typeof clearShowReCaptchaV2ForLogin;
}

enum LoginForm {
  SightXLogin = 'sightx-login',
  IncrowdLogin = 'incrowd-login',
  SingleSignOn = 'sso'
}

interface ILoginState {
  activeForm: LoginForm;
}

type Props = ILoginProps & Partial<IWithGoogleReCaptchaProps>;

class Login extends Component<Props, ILoginState> {
  private readonly formRef: React.RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);

    this.state = {
      activeForm: isPureProfileClient() ? LoginForm.SingleSignOn : LoginForm.SightXLogin
    };

    this.formRef = React.createRef<FormInstance>();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRecaptchaV2Change = this.handleRecaptchaV2Change.bind(this);
  }

  async componentDidMount() {
    const { error, userLogout, logoutSession } = this.props;

    this.props.clearShowReCaptchaV2ForLogin();

    /** TODO: all users should be logged out on the backend when needed; otherwise only the
    one that started the upgrade will be revoked access and not other members of the same account */
    if (error === 'Expired account') { // TODO: Check current upgrade process to see if this logic is still being used
      logoutSession();
      userLogout();
      return;
    }
  }

  public async componentDidUpdate(prevProps: Props) {
    const { userId, fetchWhoAmI, isLoggedIn, history, redirectUrl, token, user, isLoading, is2faEnabled, showSuccessMessage } = this.props;
    if (isLoading || !userId) {
      return;
    }

    if (isLoggedIn) {
      if (!user) {
        fetchWhoAmI();
      } else {
        // TODO 2fa is broken
        if (is2faEnabled) {
          showSuccessMessage('the code has been sent to your email.');
          history.replace('/twoFactorAuth');
          return;
        }

        const validatedUrl = getUrlToRedirect(user, redirectUrl);
        if (isNoneCoreUrl(validatedUrl)) {
          history.push(`${validatedUrl.pathname}${validatedUrl.search}`);
          return;
        }
        validatedUrl.searchParams.append('token', token);
        validatedUrl.searchParams.append('userId', user.id);
        window.location.href = validatedUrl.toString();
      }
    }
  }

  private async executeRecaptchaV3(action: string): Promise<string | null> {
    const { googleReCaptchaProps: { executeRecaptcha } = {} } = this.props;

    if (!executeRecaptcha) {
      return null;
    }

    return executeRecaptcha(action);
  }

  private async handleSubmit(values: IUserLoginRequest) {
    const { userLogin } = this.props;
    const recaptchaToken = await this.executeRecaptchaV3(RecaptchaActions.Login);

    if (!recaptchaToken) {
      showErrorMessage(RECAPTCHA_ERROR);
      return;
    }
    userLogin({
      ...values,
      recaptchaToken
    });
  }

  private handleRecaptchaV2Change(recaptchaToken: string | null) {
    const email = this.formRef.current?.getFieldValue('email');
    const password = this.formRef.current?.getFieldValue('password');
    if (recaptchaToken === null || !email) {
      return;
    }
    this.props.userLogin({
      email,
      password,
      recaptchaToken,
      useRecaptchaFallback: true
    });
  }

  private renderPasswordIcon(visible: boolean) {
    if (visible) {
      return <Icon component={EyeOnSVG} className={styles.passwordIcon} />;
    }
    return <Icon component={EyeOffSVG} className={styles.passwordIcon} />;
  }

  private changeActiveForm(form: LoginForm) {
    this.setState({ activeForm: form });
  }

  private renderIncrowdLoginForm() {
    return <LoginForIncrowd>
      <div className={styles.forgotLink}>
        <Button
          className={styles.linkButton}
          type="link"
          onClick={() => this.changeActiveForm(LoginForm.SightXLogin)}
        >
          SightX login access
        </Button>
      </div>
    </LoginForIncrowd>;
  }

  private renderEmailAndPassword() {
    return <div>
      <Form.Item className={styles.fixedHeight} label="Email" name="email" rules={[{ type: 'email' }, { required: true }]}>
        <Input
          placeholder="Email"
          className={styles.email}
          bordered={false}
        />
      </Form.Item>
      <Form.Item className={styles.fixedHeight} label="Password" name="password" rules={[{ required: true }]}>
        <Input.Password
          placeholder="∗∗∗∗∗∗∗∗∗∗"
          className={styles.password}
          bordered={false}
          iconRender={this.renderPasswordIcon}
        />
      </Form.Item>
      <div className={styles.forgotLink}>
        <Link to="/forgot-password">Forgot password?</Link>
      </div>
    </div >;
  }

  private renderSSOButton() {
    if (isFeatureEnabled(Features.Netquest) && isNetquestClient()) {
      return null;
    }
    return <div className={styles.linkButton}>
      <Button
        type="link"
        onClick={() => this.changeActiveForm(LoginForm.SingleSignOn)}
      >
        Log in with SSO
      </Button>
    </div>;
  }

  private renderSightxLoginForm() {
    const { isLoading } = this.props;
    return (
      <Form
        className={styles.form}
        onFinish={this.handleSubmit}
        validateMessages={validateMessages}
        requiredMark={false}
        ref={this.formRef}
      >
        { isPureProfileClient() ? null : <SocialLoginButtons action={CognitoAction.Login} />}
        {this.renderSSOButton()}
        <div>
          <SignUpDivider />
        </div>
        {this.renderEmailAndPassword()}
        {this.renderRecaptchaV2()}
        <HubSpotExclusionField />
        <PrimaryButton type="primary" loading={isLoading}>Sign in</PrimaryButton>
        <div className={styles.linkButton}>
          <Button
            type="link"
            href="https://sightx.io/pricing/"
          >
            Don’t have an account?
          </Button>
        </div>
      </Form>
    );
  }

  private renderForm() {
    const { activeForm } = this.state;

    switch (activeForm) {
    case LoginForm.SightXLogin:
      return this.renderSightxLoginForm();
    case LoginForm.IncrowdLogin:
      return this.renderIncrowdLoginForm();
    case LoginForm.SingleSignOn:
      return <SingleSignOnForm onCancel={() => this.changeActiveForm(LoginForm.SightXLogin)} />;
    }
  }

  private renderLogo() {
    if (isFeatureEnabled(Features.Netquest)) {
      return <Logo />;
    }
    return <div className={styles.logo}>
      <img src={sightXLogo} alt="SightX Logo" />
    </div>;
  }

  private renderRecaptchaV2() {
    if (!this.props.showReCaptchaV2) {
      return null;
    }

    return (
      <div className={styles.recaptchav2}>
        <ReCAPTCHA
          sitekey={RECAPTCHA_FALLBACK_SITE_KEY}
          onChange={this.handleRecaptchaV2Change}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className={cx(styles.loginScreen)}>
        <div className={styles.loginForm}>
          {this.renderLogo()}
          {this.renderForm()}
        </div>
        {<CuriousBanner />}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  isLoggedIn: authSelectors.getIsLoggedInState(state),
  isLoading: authSelectors.getLoadingAuthState(state),
  is2faEnabled: authSelectors.is2faEnabled(state),
  userId: authSelectors.getLoggedInUserId(state),
  redirectUrl: authSelectors.getRedirectUrl(state),
  token: authSelectors.getAuthToken(state),
  showReCaptchaV2: authSelectors.getShowReCaptchaV2ForLogin(state),
  activeView: getActiveView(state),
  user: state.auth.user,
  error: authSelectors.getAuthError(state),
  cognitoCode: getQueryParameterFromPath(state, 'code'),
  cognitoState: getQueryParameterFromPath(state, 'state')
});

const LoginWithRecaptcha3 = withGoogleReCaptcha(Login);

const ConnectedLoginContainer = connect(mapStateToProps, {
  userLogin,
  fetchWhoAmI,
  userLogout,
  showSuccessMessage,
  deleteRedirectUrl,
  logoutSession,
  clearShowReCaptchaV2ForLogin
})(LoginWithRecaptcha3);

export default withRouter(ConnectedLoginContainer);
